import React, {
  FormEvent,
  useEffect,
  useRef,
  useState,
  FunctionComponent,
} from "react";
import "./style.scss";
import { windowService } from "../../utils/window.utils";
import { QueryParamsConst } from "../../constants/query-params.constants";
import useQueryParams from "../../hooks/use-query-params.effect";
import Suggestions from "../Search/components/Suggestions";
import { StringField } from "../../types/types";
import LazySection from "../../react-only-components/LazySection";
import {
  Text,
  withSitecoreContext,
  isEditorActive,
} from "@sitecore-jss/sitecore-jss-react";

export type SearchFieldValues = {
  value: {
    href: string;
    id: string;
    linktype: string;
    title: string;
  };
};
export type SearchRootValues = {
  id: string;
};
export interface SearchBoxFields {
  "Bottom padding"?: StringField;
  "Button text"?: StringField;
  Placeholder?: StringField;
  "Box Title"?: StringField;
  "Upper padding"?: StringField;
  "Search Page"?: SearchFieldValues;
  "Search Root"?: SearchRootValues;
}
interface SearchBoxProps {
  fields?: SearchBoxFields;
  sitecoreContext: {
    pageEditing: boolean;
    language: string;
    itemId: string;
  };
}

const SearchBox: FunctionComponent<SearchBoxProps> = ({
  fields,
  sitecoreContext,
}) => {
  const isEE = sitecoreContext?.pageEditing;
  // const model = new SearchBoxModel(props)
  const bottomPadding = isEditorActive()
    ? null
    : +(fields?.["Bottom padding"]?.value ?? '0');
  const upperPadding = isEditorActive()
    ? null
    : +(fields?.["Upper padding"]?.value ?? '0');

  const [inputVal, setInputVal] = useState("");

  const [page] = useQueryParams(QueryParamsConst.PAGE, "1");
  const [perPage] = useQueryParams(QueryParamsConst.PER_PAGE, "10");
  const queryKey = QueryParamsConst.QUERY;
  const pfeInputRef = useRef<any>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const [, setSuggestionQuery] = useState("");
  const [isSuggestionsComponentVisible, setIsSuggestionsComponentVisible] =
    useState(false);

  const SUGGESTIONS_DEBOUNCE_TIME_IN_MS = 300;
  const SUGGESTIONS_MIN_CHAR_LIMIT = 3;

  let submitUrl: URL;
  try {
    const href = fields?.["Search Page"]?.value?.href ?? '';
    const origin = windowService()?.location?.origin;

    submitUrl = href.startsWith("/") ? new URL(href, origin) : new URL(href);
  } catch (e) {
    console.warn(e);
  }

  const onSubmit: (e: FormEvent<HTMLFormElement>) => void = (e) => {
    e.preventDefault();
    if (
      submitUrl &&
      pfeInputRef.current &&
      !submitUrl.searchParams.has(queryKey)
    ) {
      submitUrl.searchParams.append(queryKey, pfeInputRef.current.value);
      submitUrl.searchParams.append(QueryParamsConst.PAGE, page);
      submitUrl.searchParams.append(QueryParamsConst.PER_PAGE, perPage);
      windowService().location.href = submitUrl.toString();
    }
  };
  const onKeyUp: (e: React.KeyboardEvent<HTMLInputElement>) => void = (e) => {
    if (inputVal) {
      if (e?.key === "Enter") {
        formRef.current?.dispatchEvent(new Event("submit"));
        return;
      }
    }

    updateSuggestionsQuery(e); //debounce(updateSuggestionsQuery, SUGGESTIONS_DEBOUNCE_TIME_IN_MS)
  };

  const updateSuggestionsQuery = (event) => {
    setSuggestionQuery(event.target.value);
    //setIsSuggestionsComponentVisible(true);
  };

  const onUpdateQueryFromSuggestions = (q: string) => {
    pfeInputRef.current.value = q;
    setIsSuggestionsComponentVisible(false);
    formRef.current?.dispatchEvent(new Event("submit"));
  };

  const handleClickOutside = (event) => {
    if (formRef?.current?.contains(event.target)) {
      return;
    }

    setIsSuggestionsComponentVisible(false);
  };

  useEffect(() => {
    document && document.addEventListener("mouseup", handleClickOutside);
    return () => {
      document && document.removeEventListener("mouseup", handleClickOutside);
    };
  }, []);

  return (
    <LazySection offset={0}>
      {fields ? (
        <form
          className={`${
            isEditorActive() ? null : "search-box-wrapper"
          }`}
          action={fields?.["Search Page"]?.value?.href}
          onSubmit={onSubmit}
          ref={formRef}
          style={{
            marginTop: `-${upperPadding}px`,
            marginBottom: `${bottomPadding}px`,
          }}
          id={sitecoreContext?.itemId}
        >
          <div className="search-box-items">
            <Text
              field={fields?.["Box Title"]}
              tag="p"
              className="search-box-title"
            />

            <div className="search-box-inputWrapper">
              <input
                type="text"
                className="search-input"
                placeholder={fields?.Placeholder?.value}
                ref={pfeInputRef}
                onKeyUp={onKeyUp}
                value={inputVal}
                onChange={(e) => setInputVal(e.target.value)}
              />

              {/* <button type='submit' className='search-button'>
                  {fields?.["Button text"]?.value.toUpperCase()}
                </button> */}
              {isEditorActive() ? (
                <Text
                  field={fields?.["Button text"]}
                  className="search-button"
                  tag="div"
                />
              ) : (
                <Text
                  field={{
                    value: (fields?.["Button text"]?.value ?? '').toUpperCase(),
                  }}
                  tag="button"
                  className="search-button"
                  type="submit"
                  disabled={!inputVal}
                />
              )}
            </div>
            {isSuggestionsComponentVisible && (
              <Suggestions
                language={sitecoreContext?.language || ""}
                contextId={sitecoreContext?.itemId || ""}
                suggestionQuery={pfeInputRef?.current?.value}
                minimalLength={SUGGESTIONS_MIN_CHAR_LIMIT}
                onUpdateQuery={onUpdateQueryFromSuggestions}
              />
            )}
          </div>
        </form>
      ) : (
        (isEditorActive() || isEE) && (
          <form className={"search-box-wrapperEE"}>
            <div className="search-box-items">
              <Text
                field={fields?.["Box Title"]}
                tag="div"
                className="search-box-title"
              />

              <div className="search-box-inputWrapper">
                <input
                  type="text"
                  className="search-input"
                  placeholder={(fields as any)?.Placeholder?.value}
                />
                <Text
                  field={fields?.["Button text"]}
                  className="search-button"
                  tag="div"
                />
              </div>
            </div>
          </form>
        )
      )}
    </LazySection>
  );
};

export default withSitecoreContext()(SearchBox);
